import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Cards from '../../ReusableComponents/Cards/Cards';
import SearchBar from '../../ReusableComponents/Search/SearchBar';
import api from '../../api/api';
import CustomTable from '../../ReusableComponents/Table/CustomTable';
import en_ja from '../../translations/en_ja';

export default function Orders() {
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState([]);
  const [cardsData, setCardsData] = useState({});

  const paidStatuses = ['COMPLETED', 'AUTHORIZED','PAID'];

  const getValue = (value) => (value ? value : '-');

  const getAllOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get('/v1/admin/all/orders');
      const finalData = res?.data?.data || [];

      const mappedOrders = finalData.map((order) => ({
        id: order.id,
        order_id: order.order_id || '',
        name: order.user?.name || '',
        product: order.products?.[0]?.title || '',
        image: order.products?.[0]?.primary_image || '',
        date: order.created_at
          ? new Date(order.created_at).toLocaleDateString('en-GB')
          : '',
        price: order.total_order_amount ?? '',
        payment: paidStatuses.includes(order.payment_status)
          ? en_ja['Paid']
          : en_ja['Unpaid'],
        status: order.order_status || '',
      }));

      setOrderData(mappedOrders);
      setRows(mappedOrders);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getCardsData = async () => {
    try {
      const res = await api.get('/v1/admin/cards/ordders-data');
      setCardsData(res?.data || {});
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getAllOrders();
    getCardsData();
  }, []);

  const formatPrice = (amount) => {
    if (!amount && amount !== 0) return '-';
    if (amount === 0) return "¥0";
    return `¥${amount.toLocaleString()}`;
  };

  const columns = [
    {
      id: 'order_id',
      label: en_ja['Order Id'],
      sortable: true,
      render: (row) => <Typography>{getValue(row.order_id)}</Typography>
    },
    {
      id: 'name',
      label: en_ja['User Name'],
      sortable: true,
      render: (row) => <Typography>{getValue(row.name)}</Typography>
    },
    {
      id: 'product',
      label: en_ja['Product'],
      sortable: true,
      render: (row) => {
        const hasImage = !!row.image;
        const hasTitle = !!row.product;

        if (!hasImage && !hasTitle) {
          return <Typography>-</Typography>;
        }

        return (
          <Box display="flex" alignItems="center" gap={1.5}>
            {hasImage && (
              <Box
                component="img"
                src={row.image}
                alt={row.product || "product"}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  objectFit: "cover"
                }}
              />
            )}

            {hasTitle && (
              <Typography fontWeight={600}>
                {row.product}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'date',
      label: en_ja['Date'],
      sortable: true,
      render: (row) => <Typography>{getValue(row.date)}</Typography>
    },
    {
      id: 'price',
      label: en_ja['Total Order Amount'],
      sortable: true,
      render: (row) => (
        <Typography fontSize="14px" fontWeight="600" color="#28a745">
          {formatPrice(row.price)}
        </Typography>
      )
    },
    {
      id: 'payment',
      label: en_ja['Payment'],
      sortable: true,
      render: (row) => (
        <Typography color={row.payment === en_ja['Paid'] ? 'green' : 'red'}>
          {getValue(row.payment)}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: en_ja['Status'],
      sortable: true,
      render: (row) => {
        if (!row.status) return <Typography>-</Typography>;

        const color =
          row.status === 'Order Placed' ? 'green' :
            row.status === 'Order Not Placed' ? 'orange' : 'gray';

        return <Typography color={color}>{row.status}</Typography>;
      },
    },
  ];

  const filteredData = orderData?.filter((row) => {
    
    const term = searchTerm.toLowerCase().trim();

    return (
      String(row.order_id ?? '').toLowerCase().includes(term) ||
      String(row.name ?? '').toLowerCase().includes(term) ||
      String(row.product ?? '').toLowerCase().includes(term) ||
      String(row.date ?? '').toLowerCase().includes(term) ||
      String(row.price ?? '').toLowerCase().includes(term) ||
      String(row.payment ?? '').toLowerCase().includes(term) ||
      String(row.status ?? '').toLowerCase().includes(term)
    );
  });

  return (
    <Box sx={{ mt: 2 }}>
      {/* Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 3,
          width: '100%',
        }}
      >
        <Cards
          title={en_ja['Total Orders']}
          value={cardsData?.Total_Orders ?? orderData?.length ?? 0}
        />
        <Cards title={en_ja['New Orders']} value={cardsData?.New_Orders ?? 0} />
        <Cards title={en_ja['Completed Orders']} value={cardsData?.Completed_orders ?? 0} />
      </Box>

      <Box sx={{ my: 3 }}>
        <SearchBar
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      <CustomTable
        columns={columns}
        rows={filteredData}
        loading={loading}
        searchTerm={searchTerm}
        showIndexColumn={true}
      />
    </Box>
  );
}